import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, directory, services } from '@cwos/api-client';
import type { dashboard, services as SVC } from '@cwos/api-client';
import type { Column } from '@cwos/ui-admin';
import { authorityGate } from '../../authorityGate.js';
import { LoadFailure } from '../../LoadFailure.js';
import { nameResolver } from '../../listState.js';
import { useNav } from '../../navigation.js';
import { mayListWorkers, usePermissions } from '../../permissions.js';
import { clientNameResolver } from '../../visits/visitLogic.js';
import { useOrgCalendar } from '../../schedule/orgCalendar.js';
import {
  addDays,
  askPeriod,
  formatNumber,
  periodRangeLabel,
  weekShortLabel,
} from './analyticsRange.js';
import { LineChart } from './charts.js';
import { NOT_MEASURED_LABEL, trendPoints } from './disclosure.js';
import {
  belowUsualSentence,
  seriesTotal,
  weekChangeSentence,
  type MetricAbout,
} from './explain.js';
import { DisclosureNote, DrilldownTable, Explain, Panel } from './shared.js';

/**
 * THE ROWS BEHIND A FIGURE — the visits a KPI tile or a chart point counted,
 * read from `GET /services/scheduled` and placed in a set by the SERVER's
 * canonical bucket (`deliveredBucket`), never by a second rule written here.
 * Each row opens the visit on the Visits screen.
 *
 * A group visit is one row, exactly as the delivered count counts it once.
 * The rows are narrowed to the population the figure counted (its
 * `FigureScope`), so a program-filtered figure lists that program's visits.
 */

/** Which visits a figure counts, in the scheduled-work vocabulary. */
export type DrillSet =
  'delivered' | 'scheduled' | 'due' | 'not_started' | 'cancelled' | 'no_show' | 'unlisted';

export interface DrillRecords {
  set: DrillSet;
  /** YYYY-MM-DD, inclusive. `null` = from the start of the records. */
  from: string | null;
  /**
   * YYYY-MM-DD, EXCLUSIVE — the day after the last, exactly as a figure's
   * `PeriodLabel.to` is (the server's half-open [from, to)).
   */
  to: string;
  /** The population the figure counted — the response's own `scope`. */
  scope: dashboard.FigureScope;
}

/**
 * A hand-booked visit carries no canonical bucket (its delivery is its own
 * service event); its own status places it, the same `status → work_state`
 * map scheduled-work.ts applies to native rows.
 */
const STATUS_BUCKET: Record<string, SVC.OccurrenceBucket> = {
  scheduled: 'booked',
  confirmed: 'booked',
  completed: 'delivered',
  cancelled: 'cancelled',
  no_show: 'no_show',
};

export function bucketOf(row: SVC.ScheduledService): SVC.OccurrenceBucket | null {
  return row.deliveredBucket ?? STATUS_BUCKET[row.status] ?? null;
}

/**
 * The settle lag (owner ruling 2026-10-07): expected work counts as due once
 * its start is this far past. Mirrors `SETTLE_LAG_HOURS` in
 * apps/api/src/domains/schedule/scheduled-work.ts.
 */
const SETTLE_LAG_MS = services.SETTLE_LAG_HOURS * 3_600_000;

/** `isDue` (scheduled-work.ts) over a visit row: an outcome, or expected work past the settle lag. */
function isDue(bucket: SVC.OccurrenceBucket | null, startsAt: string, now: Date): boolean {
  if (bucket === 'delivered' || bucket === 'no_show') return true;
  const expected = bucket === 'booked' || bucket === 'not_started' || bucket === 'other_status';
  return expected && Date.parse(startsAt) < now.getTime() - SETTLE_LAG_MS;
}

export function inDrillSet(row: SVC.ScheduledService, set: DrillSet, now: Date): boolean {
  const b = bucketOf(row);
  switch (set) {
    case 'delivered':
      return b === 'delivered';
    case 'cancelled':
      return b === 'cancelled';
    case 'no_show':
      return b === 'no_show';
    case 'unlisted':
      return b === 'vendor_absent';
    case 'scheduled':
      return b !== null && b !== 'cancelled' && b !== 'vendor_absent';
    case 'due':
      return isDue(b, row.startsAt, now);
    case 'not_started':
      return isDue(b, row.startsAt, now) && b !== 'delivered' && b !== 'no_show';
  }
}

/**
 * Whether a visit is in the figure's population. Rows carry the visit's
 * program, matched as the server's program filter matches it; they carry no
 * site, so a site-narrowed figure is not listed at all (see the table).
 */
export function inFigureScope(row: SVC.ScheduledService, scope: dashboard.FigureScope): boolean {
  if (scope.population === 'organization') return true;
  if (scope.population === 'none') return false;
  return (
    scope.programIds == null || (row.programId != null && scope.programIds.includes(row.programId))
  );
}

/**
 * The 7 days of a week, clipped to the period it was drawn for. `to` is
 * exclusive on both sides: the day after the week, or the period's own end.
 */
export function weekWindow(
  weekStart: string,
  period: { from: string | null; to: string },
): { from: string; to: string } {
  const end = addDays(weekStart, 7);
  return {
    from: period.from && period.from > weekStart ? period.from : weekStart,
    to: end > period.to ? period.to : end,
  };
}

/**
 * Minutes as the "Service hours delivered" tile shows them: rounded to two
 * decimals as the server rounds them, then shown at that headline's precision
 * (1, analyticsService.ts) — so the caption and the tile print the same number.
 */
const hoursLabel = (minutes: number): string =>
  formatNumber(Math.round((minutes / 60) * 100) / 100, 1);

const BUCKET_LABEL: Record<SVC.OccurrenceBucket, string> = {
  delivered: 'Delivered',
  booked: 'Booked',
  not_started: 'Not started',
  cancelled: 'Cancelled',
  no_show: 'No-show',
  vendor_absent: 'No longer in Ritten',
  other_status: 'Other Ritten status',
};

/** "Delivered · Started (empty)" — the bucket, with the lifecycle as a sub-label. */
function statusLabel(row: SVC.ScheduledService): string {
  const b = bucketOf(row);
  const head = b ? BUCKET_LABEL[b] : 'Unknown';
  const sub = row.occurrenceState ? services.OCCURRENCE_STATE_LABELS[row.occurrenceState] : null;
  if (row.sourceStatusConflict) return `${head} · on the encounter’s minutes`;
  return sub && sub !== head ? `${head} · ${sub}` : head;
}

export function DrillRecordsTable({ records, now }: { records: DrillRecords; now?: Date }) {
  const permissions = usePermissions();
  const navigate = useNav();
  const allowed = permissions.has('service:read');
  const canListWorkers = mayListWorkers(permissions);
  // The ORGANIZATION's days, as the figure counted them: [from, to) in its zone.
  const calendar = useOrgCalendar();
  const bounds = useMemo(
    () => services.dayWindow(records.from, records.to, calendar.timeZone),
    [records.from, records.to, calendar.timeZone],
  );
  // A visit's site is its encounter's, which the visit list does not carry:
  // the rows behind a site-narrowed figure cannot be told apart, so none are listed.
  const siteNarrowed = records.scope.population === 'subset' && records.scope.siteIds != null;

  // Discharged clients' visits INCLUDED: the canonical count keeps them, so
  // the rows behind it must, or the list comes up short of the figure.
  const visits = useQuery({
    queryKey: ['services', 'scheduled', 'drill', bounds.from, bounds.to],
    queryFn: () => services.scheduled({ ...bounds, includeDischarged: true }),
    enabled: allowed && !siteNarrowed,
  });
  const workers = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canListWorkers,
  });
  const clients = useQuery({
    queryKey: ['directory', 'clients'],
    queryFn: () => directory.clients(),
    enabled: permissions.has('directory:read'),
  });
  const nameOf = useMemo(() => nameResolver(workers, canListWorkers), [workers, canListWorkers]);
  const directoryGate = authorityGate(permissions, 'directory:read').kind;
  const clientOf = useMemo(
    () => clientNameResolver(directoryGate, clients),
    [directoryGate, clients],
  );

  const at = now ?? new Date();
  const rows = visits.data?.scheduledServices
    .filter((r) => inFigureScope(r, records.scope) && inDrillSet(r, records.set, at))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  const columns: Column<SVC.ScheduledService>[] = [
    {
      key: 'when',
      header: 'When',
      render: (r) =>
        new Date(r.startsAt).toLocaleString(undefined, {
          timeZone: calendar.timeZone,
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }),
    },
    { key: 'status', header: 'Status', render: statusLabel },
    {
      key: 'staff',
      header: 'Staff',
      render: (r) => {
        const ids = r.workerIds ?? (r.workerId ? [r.workerId] : []);
        return ids.length === 0 ? 'No staff recorded' : ids.map(nameOf).join(', ');
      },
    },
    { key: 'client', header: 'Client', render: (r) => clientOf(r.clientId) },
    {
      key: 'minutes',
      header: 'Service minutes',
      render: (r) => (r.serviceMinutes == null ? '—' : String(r.serviceMinutes)),
      numeric: true,
    },
  ];
  const minutes = rows?.reduce((sum, r) => sum + (r.serviceMinutes ?? 0), 0) ?? 0;
  // An older API sends no serviceMinutes at all: then there are no hours to
  // add up, and "0 service hours" would be an unavailable source shown as 0.
  const minutesSent = rows?.some((r) => r.serviceMinutes !== undefined) ?? false;

  if (siteNarrowed) {
    return (
      <DisclosureNote
        heading="Visits not listed for a site"
        text="This figure counts one site’s visits, and the visit list does not carry a visit’s site, so the visits behind it cannot be listed without other sites’ visits. Clear the site filter to list them."
      />
    );
  }

  return (
    <div className="an-stack">
      {rows ? (
        <p className="rn-muted" role="status" style={{ margin: 0 }}>
          {rows.length} {rows.length === 1 ? 'visit' : 'visits'}
          {records.set === 'delivered' && minutesSent
            ? `, ${hoursLabel(minutes)} service hours (the service minutes below added up)`
            : ''}
          .{' '}
          {records.set === 'delivered'
            ? 'A delivered service with no calendar visit — an encounter dated outside the imported calendar, or one recorded with no visit booked — counts in the figure but has no row here. '
            : ''}
          {calendar.known
            ? `Days are the organization’s, in ${calendar.timeZone}.`
            : 'Days are in this browser’s time zone.'}{' '}
          Open a visit to see it on the Visits screen.
          {visits.data?.scoped
            ? ' Your access narrows these rows to your own visits and your reports’, so they can be fewer than the figure counts.'
            : ''}
        </p>
      ) : null}
      <DrilldownTable
        withheld={!permissions.isLoading && !allowed}
        rows={rows}
        isLoading={visits.isLoading}
        isError={visits.isError}
        error={visits.error}
        onRetry={() => void visits.refetch()}
        columns={columns}
        rowKey={(r) => r.id}
        onRowOpen={(r) => navigate(`/visits?occurrence=${encodeURIComponent(r.id)}`)}
        emptyHeading="No visits"
        emptyMessage="No visits in this window fall in this group."
      />
    </div>
  );
}

/** One opened figure: what it means, and the rows behind it. */
export interface Drill {
  title: string;
  about: MetricAbout;
  change?: string | null;
  attention?: string | null;
  /** The question "Ask Phibian about this" seeds. */
  ask?: string;
  /** The visits behind the figure. */
  records?: DrillRecords;
  /** A figure whose rows are not visits (the roster, the time clock): where they live. */
  elsewhere?: { label: string; onOpen: () => void };
}

/**
 * The panel a KPI tile or a chart point opens. It takes focus when it opens,
 * so a keyboard or screen-reader user lands on what they asked for rather than
 * on a tile above it.
 */
export function DrillPanel({ drill, onClose }: { drill: Drill; onClose: () => void }) {
  const focusOnOpen = useCallback((el: HTMLHeadingElement | null) => {
    el?.scrollIntoView?.({ block: 'nearest' });
    el?.focus();
  }, []);
  return (
    <section className="rn-card" aria-label={drill.title}>
      <div className="rn-card-head">
        <h2 className="rn-card-title" tabIndex={-1} ref={focusOnOpen}>
          {drill.title}
        </h2>
        <button type="button" className="rn-btn-quiet" onClick={onClose}>
          Close
        </button>
      </div>
      <Explain
        about={drill.about}
        change={drill.change}
        attention={drill.attention}
        ask={drill.ask}
        open
      />
      {drill.records ? (
        <div style={{ marginTop: 'var(--space-12)' }}>
          <DrillRecordsTable records={drill.records} />
        </div>
      ) : null}
      {drill.elsewhere ? (
        <div style={{ marginTop: 'var(--space-12)' }}>
          <button type="button" className="rn-btn-quiet" onClick={drill.elsewhere.onOpen}>
            {drill.elsewhere.label}
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** One line on a weekly chart, and the visits a point of it counts. */
export interface WeeklyLine {
  name: string;
  color: string;
  dashed?: boolean;
  series: dashboard.TrendSeries;
  /** The visits a point of this line counts — omitted for a line with no visit rows. */
  set?: DrillSet;
}

/** The axis every weekly payload shares (`AnalyticsTrends`, care-gap and documentation trends). */
export interface WeeklyAxis {
  period: dashboard.PeriodLabel;
  weeks: string[];
  coverage: dashboard.WeekCoverage[];
  scope: dashboard.FigureScope;
}

const unmeasured = (s: dashboard.TrendSeries): string | null => {
  const first = s.points[0];
  return first?.kind === 'no_source' && s.points.every((p) => p.kind === 'no_source')
    ? first.message
    : null;
};

/**
 * A WEEKLY CHART THAT EXPLAINS ITSELF AND OPENS TO ITS ROWS.
 *
 * - Follows the period picker (the axis is the server's), with the week to date
 *   and clipped weeks drawn as such.
 * - A series with no source at all is "Not measured" and the server's reason,
 *   never a flat zero line.
 * - A point opens the visits that week counted; "Show the visits" opens the
 *   whole period, which is also the keyboard path.
 * - The note under it is arithmetic over the first line: the latest complete
 *   week against the one before, the total when the series is additive, and a
 *   week well below the usual one.
 */
export function WeeklyPanel({
  title,
  axis,
  lines,
  about,
  additive = false,
  formatValue = String,
  onDrill,
}: {
  title: string;
  axis: WeeklyAxis;
  lines: WeeklyLine[];
  about: MetricAbout;
  /** True when weekly points sum to the period figure (counts and hours, not distinct counts). */
  additive?: boolean;
  formatValue?: (v: number) => string;
  onDrill?: (drill: Drill) => void;
}) {
  const subtitle = `weekly · ${periodRangeLabel(axis.period)}`;
  const primary = lines[0]!;
  const notMeasured = unmeasured(primary.series);
  if (notMeasured !== null) {
    return (
      <Panel title={title} subtitle={subtitle}>
        <DisclosureNote heading={NOT_MEASURED_LABEL} text={notMeasured} />
      </Panel>
    );
  }
  const labels = axis.weeks.map(weekShortLabel);
  const total = additive ? seriesTotal(primary.series) : null;
  const change = weekChangeSentence(primary.series, axis.coverage, labels, formatValue);
  const drillWeek = (seriesIndex: number, pointIndex: number) => {
    const line = lines[seriesIndex];
    const week = axis.weeks[pointIndex];
    if (!onDrill || !line?.set || !week) return;
    onDrill({
      title: `${line.name}, week of ${labels[pointIndex]}`,
      about,
      change,
      ask: `Why was ${line.name.toLowerCase()} what it was in the week of ${labels[pointIndex]}?`,
      records: { set: line.set, scope: axis.scope, ...weekWindow(week, axis.period) },
    });
  };
  return (
    <Panel title={title} subtitle={subtitle}>
      <LineChart
        labels={labels}
        coverage={axis.coverage}
        formatValue={formatValue}
        label={title}
        series={lines.map((l) => ({
          name: l.name,
          color: l.color,
          dashed: l.dashed,
          points: trendPoints(l.series),
        }))}
        onPointClick={onDrill && lines.some((l) => l.set) ? drillWeek : undefined}
      />
      <Explain
        about={about}
        change={
          total === null
            ? change
            : `${change} Over the period the weekly points add up to ${formatValue(total)}.`
        }
        attention={belowUsualSentence(primary.series, axis.coverage, labels, formatValue)}
        ask={`What changed in ${title.toLowerCase()} over ${askPeriod(axis.period)}, and why?`}
      />
      {onDrill && primary.set ? (
        <button
          type="button"
          className="rn-btn-quiet"
          style={{ marginTop: 'var(--space-8)' }}
          onClick={() =>
            onDrill({
              title: `${title}, ${axis.period.label.toLowerCase()}`,
              about,
              change,
              records: {
                set: primary.set!,
                from: axis.period.from,
                to: axis.period.to,
                scope: axis.scope,
              },
            })
          }
        >
          Show the visits
        </button>
      ) : null}
    </Panel>
  );
}

/**
 * A weekly chart that could not be drawn. A period with too many weeks is
 * REFUSED by the server (422) with its own sentence — "choose a shorter
 * period" is the answer, so it is said, with no Try again. Anything else is a
 * load failure.
 */
export function TrendFailure({
  error,
  message,
  onRetry,
}: {
  error: unknown;
  message: string;
  onRetry: () => void;
}) {
  if (error instanceof ApiError && error.status === 422) {
    const said = (error.body as { message?: unknown } | null)?.message;
    return (
      <DisclosureNote
        heading="No weekly chart for this period"
        text={typeof said === 'string' ? said : error.message}
      />
    );
  }
  return <LoadFailure error={error} message={message} onRetry={onRetry} />;
}
